import React, { useCallback, useState, forwardRef } from 'react';
import StepEvaluationCriteria from '@components/Evaluation/StepEvaluationCriteria';
import SuggestionChips from '@components/core/SuggestionChips';
import AttributesManager from '@components/Attributes/AttributesManager';
import TAHireAssistants from '@components/AiAssistant/TAHireAssistants';
import AiAssistant from '@components/AiAssistant';
import fetchCustom from '@helpers/fetchCustom';
import { appendQueryParams } from '@helpers/helpers';
import SectionTitle from '@components/core/SectionTitle';
import SelectDropdown, { SELECT_DROPDOWN_SIZE } from '@components/core/SelectDropdown';
import EvaluationMainActions from '@components/Evaluation/EvaluationMainActions';
import { useRequirementsTab } from './context';
import { useTranslations } from '@components/context/TranslationContext';
import { notification } from '@utils/utils';
import './index.less';
import Tooltip from '@components/core/Tooltip';
import Skeleton from '../components/Skeleton';
import logAbortableError from '@helpers/logAbortableError';

const RequirementsTab = forwardRef((props, ref) => {
    const {
        attributes,
        onUpdateCriteriaHandler,
        onSelectStepHandler,
        onSkillAddHandler,
        onRemoveCriteriaHandler,
        onDuplicateCriteriaHandler,
        anyStepRemaining,
        onRemoveAttribute,
        onAddEvaluationClickHandler,
        loading,
        attributesConfig,
        locale,
        isStepEvaluated,
        ratingOptions,
        isAttributeEvaluated,
        suggestedAttributesSet,
        setSuggestedAttributesSet,
        aiJobSkillsRecommendation,
        evaluationCriteria,
        attributesOptions,
        getAttributeTitleByKey,
        getStepOptions,
        evaluationTemplatesOptions = [],
        config,
        applyEvaluationTemplate,
        onAutofillEvaluationCriteria,
        saveAttributeHandler,
        computedData,
        hasAnyStepEvaluated,
    } = useRequirementsTab();

    const { t } = useTranslations();
    const [saving, setSaving] = useState(false);
    const [savingAttributes, setSavingAttributes] = useState({}); //Keep the saving state for suggested skills
    const [applyEvaluationTemplateLoading, setApplyEvaluationTemplateLoading] = useState(false);

    const handleAddSuggestedAttribute = async (id, value, config) => {
        const { save_url, locale, url } = config;
        if (saving) return true;
        const groupedAttribute = `${id}:${value}`;
        try {
            setSaving(true);
            setSavingAttributes((prevData) => ({ ...prevData, [groupedAttribute]: true }))
            const response = await fetchCustom(
                appendQueryParams(url, {
                    lang: locale,
                    'values[]': encodeURIComponent(value),
                })
            );
            const data = await response.json();
            const existingAttribute = data.results.find(
                (item) => item.text.toLowerCase() === value.toLowerCase()
            );
            const newAttribute = { value: value, isNew: true, selected: false, text: value };
            const filteredAttributes = suggestedAttributesSet?.[id]?.filter(
                (attribute) => value.toLowerCase() !== attribute.toLowerCase()
            );
            setSuggestedAttributesSet((prevData) => ({
                ...prevData,
                [id]: filteredAttributes || [],
            }));
            await onAttributeChangeHandler({
                id,
                value,
                result: { data: existingAttribute || newAttribute },
                save_url,
                locale,
            });
        } catch (e) {
            logAbortableError('Unable to get suggested attribute data in search', e);
            notification(global_i18n('ajax_fail_notification'), { type: 'error' });
        } finally {
            setSaving(false);
            setSavingAttributes((prevData) => ({ ...prevData, [groupedAttribute]: false }));
        }
    };

    const onAttributeChangeHandler = async (config) => {
        if (saving) return;
        setSaving(true);
        try {
            await saveAttributeHandler(config);
        } catch (err) {
            logAbortableError('Error saving attribute:', err);
            notification(err.message || global_i18n('ajax_fail_notification'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const getAndApplyEvaluationTemplate = async (value) => {
        setApplyEvaluationTemplateLoading(true);
        const url = config.get_evaluation_template_url.replace(':template_id', value);
        const response = await fetchCustom(url);
        const data = await response.json();
        const templateData = {
            attributes: data.grouped_attributes,
            evaluationCriteria: data.evaluation_criteria,
        };
        applyEvaluationTemplate(templateData);
        setApplyEvaluationTemplateLoading(false);
        notification(t('Evaluation template applied successfully'), { type: 'success' });
    };

    const handleEvaluationTemplateChange = useCallback(async (templateId) => {
        const { hasAttributes, hasSteps } = computedData;
        if (!config.get_evaluation_template_url || !templateId) {
            notification(global_i18n('ajax_fail_notification'), { type: 'error' });
            return;
        }

        if (!hasAttributes && !hasSteps) {
            getAndApplyEvaluationTemplate(templateId);
            return;
        }

        show_confirmation_overlay(
            'apply-evaluation-template-overlay',
            t('Apply evaluation template'),
            t(
                'Are you sure you want to apply this evaluation template? This will overwrite the current data.'
            ),
            {
                label: t('Cancel'),
            },
            {
                label: t('Apply'),
                action: () => getAndApplyEvaluationTemplate(templateId),
            }
        );
    }, [computedData, getAndApplyEvaluationTemplate, config.get_evaluation_template_url]);
    
    if (loading) return <Skeleton type="evaluation" />;

    return (
        <div className='requirements-section' ref={ref}>
            <SectionTitle title={t('Job requirements')}>
                {Object.keys(aiJobSkillsRecommendation).length > 0 && (
                    <div className='ai-assistant-recommended-wrapper'>
                        <AiAssistant
                            assistantData={aiJobSkillsRecommendation}
                            Assistants={TAHireAssistants}
                            notify={notification}
                        />
                    </div>
                )}
                <div className='evaluation-templates-container'>
                    {applyEvaluationTemplateLoading ? (
                        <div className='evaluation-template-loading'>
                            {t('Applying evaluation template...')}
                        </div>
                    ) : (
                        <Tooltip title={hasAnyStepEvaluated ? t('You cannot apply a template if any applicant has already been evaluated.') : ''}>
                            <SelectDropdown
                                disabled={hasAnyStepEvaluated}
                                placeholder={t('Select evaluation template')}
                                className='evaluation-template-select'
                                options={evaluationTemplatesOptions}
                                value={null}
                                onChange={(value) => value != null && handleEvaluationTemplateChange(value)}
                                size={SELECT_DROPDOWN_SIZE.LG}
                                portal
                                clearOnChange
                            />
                        </Tooltip>
                    )}
                </div>
            </SectionTitle>
            <AttributesManager
                source="job_ad"
                onRemoveAttribute={onRemoveAttribute}
                attributesConfig={attributesConfig}
                attributesSet={attributes}
                onChange={onAttributeChangeHandler}
                locale={locale}
                loading={saving}
            >
                {({ id, save_url, url }) =>
                    suggestedAttributesSet[id]?.length > 0 && (
                        <SuggestionChips
                            label={t('AI suggestions')}
                            items={suggestedAttributesSet[id] || []}
                            loadingIds={Object.keys(savingAttributes)
                                .filter((key) => savingAttributes[key] && key.startsWith(`${id}:`))
                                .map((key) => key.slice(id.length + 1))}
                            disabled={saving}
                            onSelect={({ id: attribute }) => {
                                if (saving) return;
                                handleAddSuggestedAttribute(id, attribute, {
                                    save_url,
                                    locale,
                                    url,
                                });
                            }}
                        />
                    )
                }
            </AttributesManager>
            <SectionTitle title={t('Evaluation steps')} />
            <div className='evaluation-steps-container'>
                {evaluationCriteria.map((criteria, index) => (
                    <StepEvaluationCriteria
                        key={index + criteria.step_id}
                        criteria={criteria}
                        updateCriteria={(updatedCriteria) =>
                            onUpdateCriteriaHandler(updatedCriteria, index)
                        }
                        onChange={(value) => onSelectStepHandler(value, index)}
                        onAddSkill={(value) => onSkillAddHandler(value, index)}
                        onRemoveCriteria={() => onRemoveCriteriaHandler(index)}
                        onDuplicate={() => onDuplicateCriteriaHandler(index)}
                        canDuplicate={anyStepRemaining}
                        isDisabled={isStepEvaluated(criteria.step_id)}
                        steps={getStepOptions(criteria.step_id)}
                        attributesOptions={attributesOptions}
                        ratingOptions={ratingOptions}
                        isAttributeEvaluated={isAttributeEvaluated}
                        getAttributeTitle={getAttributeTitleByKey}
                    />
                ))}
            </div>
            {anyStepRemaining && (
                <EvaluationMainActions
                    onAutofillClick={onAutofillEvaluationCriteria}
                    onAddClick={onAddEvaluationClickHandler}
                />
            )}
        </div>
    );
});

export default RequirementsTab;
